/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Grid, Stack, Typography, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DetailsIcon from '../../../../../../../../../assets/Resources/DetailIcon.svg';
import CategoriesIcon from '../../../../../../../../../assets/Resources/CategoriesIcon.svg';
import AcessIcon from '../../../../../../../../../assets/Resources/AccessIcon.svg';
import { IoPricetagOutline } from 'react-icons/io5';
import { FaRegEdit } from 'react-icons/fa';
import { PiClockCountdownBold } from 'react-icons/pi';
import { LanguageContext } from '../../../../../../../../../libs/Context';
import { ImageItem } from '../types';
import { ImageUpdateOptions } from '../ImageUploadHelper';

// Import components from Reusable folder
import DetailsSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/DetailsSlider';
import CategorySlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/CategorySlider';
import TagSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/TagsSlider';
import AccessSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/AccessSlider';
import ManageContentSlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/ManageContentSlider';
import ActivitySlider from '../../../../Components/Reusable/RightBarOptions/Components/SliderContent/Components/ActivitySlider';
import { image } from 'html2canvas/dist/types/css/types/image';
import useContentPermissions from '../../../Reusable/ContentCards/useContentPermissions';
import { selectedTabs } from '../../../../../constants';

interface ImageDetailsSliderProps {
  selectedTab?: selectedTabs;
  setSelectedTab: (tab: selectedTabs) => void;
  setOpen?: (open: boolean) => void;
  articleCreate?: string;
  imageData?: any;
  selectedItems?: string[];
  imagesList?: ImageItem[];
  companyId?: string;
  onSave?: (updatedData: any, imageIds: string[]) => void;
  open?: boolean;
  onClose?: () => void;
  isDisabled?: boolean;
}

// Define the access control data structure
interface AccessControlData {
  internalEmployees?: {
    allSelected?: boolean;
    departments?: Array<{name: string; count?: number}>;
    specificUsers?: Array<{userId: string; name: string; email: string; role?: string}>;
  };
  userGroups?: Array<{type: string; count?: number}>;
  participantCount?: number;
  participantLimit?: number;
}

// Define a consolidated UI state interface for image data
interface ImageUIState {
  details: {
    title: string;
    description: string;
    url?: string;
    dimensions?: {
      width: number;
      height: number;
    };
    fileSize?: number;
    fileType?: string;
    thumbnail?: {
      url: string;
      canEdit?: boolean;
      canDelete?: boolean;
    };
  };
  categoryIds: string[];
  tags: string[];
  access: {
    visibility?: any;
    allowDownload?: boolean;
    allowShare?: boolean;
    accessControl?: AccessControlData;
  };
  manage: {
    status?:any;
    publishDate?: string;
  };
}

/**
 * ImageDetailsSlider - A specialized slider for image content details
 */
const ImageDetailsSlider: React.FC<ImageDetailsSliderProps> = (props) =>
{
	const {
		selectedTab = 'Details',
		setSelectedTab,
		setOpen,
		articleCreate,
		imageData,
		selectedItems = [],
		onSave
	} = props;

	const { language } = useContext(LanguageContext);

	const { isPrivateShareEnabled, isShareEnabled, isDownloadEnabled } = useContentPermissions();

	// Define the tabs specific to image editing - memoized to prevent unnecessary re-creation
	const imageTabs = useMemo(() =>
	{
		// Base tabs that are always available
		const baseTabs = [
			{ name: 'Categories', Icon: CategoriesIcon, ImageIcon: true },
			{ name: 'Add Tags', Icon: <IoPricetagOutline fontSize={'26px'} />, ImageIcon: false },
			{ name: 'Access', Icon: AcessIcon, ImageIcon: true }
			// { name: 'Activity', Icon: <PiClockCountdownBold fontSize={'26px'} />, ImageIcon: false }
		];

		// Only add the Details tab if a single image is selected
		if (!selectedItems || selectedItems.length <= 1)
		{
			return [
				{ name: 'Details', Icon: DetailsIcon, ImageIcon: true },
				...baseTabs
			];
		}

		return baseTabs;
	}, [ selectedItems ]);

	// Update selectedTab if Details is selected but no longer available
	useEffect(() =>
	{
		if (selectedTab === 'Details' && selectedItems && selectedItems.length > 1)
		{
			// Switch to first available tab if Details is not available
			if (imageTabs.length > 0)
			{
				setSelectedTab(imageTabs[0].name as selectedTabs);
			}
		}
	}, [ selectedItems, selectedTab, imageTabs, setSelectedTab ]);

	// Simple function to create initial state from image data or empty defaults
	const createInitialState = useCallback((): ImageUIState =>
	{
		return {
			details : {
				title       : imageData?.title || '',
				description : imageData?.description || '',
				dimensions  : imageData?.dimensions || undefined,
				fileSize    : imageData?.fileSize || undefined,
				fileType    : imageData?.fileType || undefined,
				url         : imageData?.url || ''
				// thumbnail   : (imageData?.coverImage || imageData?.thumbnail)
				// 	? {
				// 		url       : imageData.coverImage || imageData.thumbnail?.url || '',
				// 		canEdit   : imageData?.thumbnail?.canEdit || '',
				// 		canDelete : imageData?.thumbnail?.canDelete || ''
				// 	}
				// 	: undefined
			},
			categoryIds : imageData?.categoryIds || [],
			tags        : imageData?.tags || [],
			access      : {
				// visibility    : imageData?.visibility !== undefined ? (imageData?.visibility as 'public' | 'private') : isPrivateShareEnabled ? 'private' : 'public',
				allowDownload : imageData?.allowDownload !== undefined ? imageData?.allowDownload : isDownloadEnabled,
				allowShare    : imageData?.allowShare !== undefined ? imageData?.allowShare : isShareEnabled
				// accessControl : imageData?.accessControl || ''
			},

			manage : {
				status      : imageData?.status || '',
				publishDate : imageData?.publishDate || ''
			}
		};
	}, [ imageData ]);

	// Store original state for comparison
	const [ originalData, setOriginalData ] = useState<ImageUIState | null>(null);

	// Simplified state management - just use the image data or empty defaults
	const [ uiState, setUiState ] = useState<ImageUIState>(createInitialState);

	// Reset state when selection changes
	useEffect(() =>
	{
		const newState = createInitialState();

		setUiState(newState);
		setOriginalData(newState);
	}, [ imageData, createInitialState ]);

	// Detect changes in state
	const hasChanges = useMemo(() =>
	{
		if (!originalData) return false;

		// Simple check for most common fields that might change
		return (
			originalData.details.title !== uiState.details.title ||
      originalData.details.description !== uiState.details.description ||
      JSON.stringify(originalData.categoryIds) !== JSON.stringify(uiState.categoryIds) ||
      JSON.stringify(originalData.tags) !== JSON.stringify(uiState.tags) ||
      // originalData.access.visibility !== uiState.access.visibility ||
      originalData.access.allowDownload !== uiState.access.allowDownload ||
      originalData.access.allowShare !== uiState.access.allowShare
		);
	}, [ originalData, uiState ]);

	// Generic state updater function that accepts a field path and a value or updater function
	const updateState = useCallback(<K extends keyof ImageUIState>(
		section: K,
		valueOrUpdater: ImageUIState[K] | ((prev: ImageUIState[K]) => ImageUIState[K])
	) =>
	{
		setUiState((prevState) =>
		{
			const newValue = typeof valueOrUpdater === 'function'
				? (valueOrUpdater as Function)(prevState[section])
				: valueOrUpdater;

			return {
				...prevState,
				[section] : newValue
			};
		});
	}, []);

	// Handle updates to details section (title, description, etc.)
	const handleDetailsUpdate = useCallback((detailsData: any) =>
	{
		updateState('details', (prev) => ({
			...prev,
			...detailsData
		}));
	}, [ updateState ]);

	// Handle updates to categories
	const handleCategoriesUpdate = useCallback((categories: string[]) =>
	{
		updateState('categoryIds', categories);
	}, [ updateState ]);

	// Handle updates to tags
	const handleTagsUpdate = useCallback((tags: string[]) =>
	{
		updateState('tags', tags);
	}, [ updateState ]);

	// Handle updates to access control settings
	const handleAccessUpdate = useCallback((accessData: any) =>
	{
		updateState('access', (prev) => ({
			...prev,
			// visibility    : accessData.visibility || prev.visibility,
			allowDownload : accessData.allowDownload !== undefined ? accessData.allowDownload : prev.allowDownload,
			allowShare    : accessData.allowShare !== undefined ? accessData.allowShare : prev.allowShare
			// accessControl : accessData.accessControl || prev.accessControl
		}));
	}, [ updateState ]);

	// Enhanced save function to handle multiple images
	const handleSave = useCallback(() =>
	{
		const updatedImageData: any = {
			...(uiState.details.title && { title: uiState.details.title }),
			...(uiState.details.description && { description: uiState.details.description }),
			...(uiState.details.url && { url: uiState.details.url }),
			// ...(uiState.details.thumbnail && {
			// 	thumbnail : {
			// 		url       : uiState.details.thumbnail.url,
			// 		canEdit   : uiState.details.thumbnail.canEdit,
			// 		canDelete : uiState.details.thumbnail.canDelete
			// 	}
			// }),
			// Only include categoryIds if we're on the Categories tab
			// ...(uiState.categoryIds.length > 0 && { categoryIds: uiState.categoryIds }),
			...(uiState.tags.length > 0 && { tags: uiState.tags }),
			// ...(uiState.access.visibility && { visibility: uiState.access.visibility }),
			...(uiState.access.allowDownload !== undefined && { allowDownload: uiState.access.allowDownload }),
			...(uiState.access.allowShare !== undefined && { allowShare: uiState.access.allowShare }),
			...(uiState.manage.status && { status: uiState.manage.status })
			// ...(uiState.access.accessControl && { accessControl: uiState.access.accessControl })
		};

		if (uiState.categoryIds?.length > 0 && uiState.categoryIds?.[0] !== imageData?.categoryIds?.[0])
		{
			updatedImageData.categoryIds = uiState.categoryIds;
		}

		if (onSave)
		{
			if (selectedItems && selectedItems.length > 0)
			{
				// Ensure we're passing valid image IDs
				// const validImageIds = selectedItems.filter((id) => typeof id === 'string' && id.trim() !== '');

				// if (validImageIds.length > 0)
				// {
				onSave(updatedImageData, selectedItems);
				// }
				// else
				// {
				// 	console.error('No valid image IDs found in selectedItems');
				// }
			}
			else if (imageData)
			{
				onSave(updatedImageData, [ imageData.id ? imageData.id : imageData?._id ]);
			}
		}

		setOpen?.(false);
	}, [ uiState, onSave, setOpen, imageData, selectedItems, selectedTab ]);

	// Memoize the tab content to prevent unnecessary re-renders
	const renderTabContent = useMemo(() =>
	{
		switch (selectedTab)
		{
			case 'Details':
				// Only render Details content if a single image is selected
				if (selectedItems && selectedItems.length > 1)
				{
					return null;
				}

				return (
					<DetailsSlider
						articleCreate={articleCreate}
						data={uiState.details}
						itemType='image'
						onChange={handleDetailsUpdate}
					/>
				);
			case 'Categories':
				return (
					<CategorySlider
						artcileCreate={articleCreate}
						categories={uiState.categoryIds}
						onChange={handleCategoriesUpdate}
					/>
				);
			case 'Add Tags':
				return (
					<TagSlider
						tags={imageData?.tags || []}
						initialSelectedTags={uiState.tags}
						onChange={handleTagsUpdate}
					/>
				);
			case 'Access':
				return (
					<AccessSlider
						visibility={uiState.access.visibility}
						allowDownload={uiState.access.allowDownload}
						allowShare={uiState.access.allowShare}
						accessControl={uiState.access.accessControl}
						onChange={handleAccessUpdate}
					/>
				);
			// case 'Manage':
			// 	return (
			// 		<ManageContentSlider />
			// 	);
			case 'Activity':
				return (
					<ActivitySlider />
				);
			default:
				return null;
		}
	}, [
		selectedTab,
		selectedItems,
		articleCreate,
		uiState,
		imageData,
		handleDetailsUpdate,
		handleCategoriesUpdate,
		handleTagsUpdate,
		handleAccessUpdate
	]);

	return (
		<Grid container height='100%' width='100%' >
			{/* Left Sidebar Tabs */}
			<Grid item xs={2.5} height={'100%'} sx={{ boxShadow: '0px 4px 14px 0px rgba(0, 0, 0, 0.10)' }}>
				<Stack spacing={2} pt={3}>
					{imageTabs.map((tab, index) => (
						<Stack
							key={index}
							direction='column'
							alignItems='center'
							sx={{
								cursor          : 'pointer',
								padding         : '10px 8px',
								borderRight     : selectedTab === tab.name ? '4px solid #24a4c4' : '4px solid transparent',
								backgroundColor : selectedTab === tab.name ? '#EFFBFF' : ''
							}}
							onClick={() => setSelectedTab(tab.name as selectedTabs)}
						>
							{tab?.ImageIcon
								? (
									<Box width={'28px'} height={'28px'}>
										<img src={`${tab.Icon}`} alt='' className='invertGray' />
									</Box>
								)
								: (tab.Icon)}
							<Typography variant='body2' color={'#737373'} pt={0.2}>{tab.name}</Typography>
						</Stack>
					))}
				</Stack>
			</Grid>

			{/* Right Content Area */}
			<Grid item xs={9.5} height={'100%'} width={'100%'}>
				<Grid container height={'100%'} direction={'column'} display={'flex'}>
					<Grid item px={2} py={1}>
						<Stack direction={'row'} alignItems={'center'} display={'flex'} justifyContent={'space-between'}>
							<Typography fontSize={'22px'} fontWeight={'var(--font500)'}>{selectedTab}</Typography>
							<IconButton onClick={() => setOpen?.(false)}>
								<CloseIcon />
							</IconButton>
						</Stack>
					</Grid>
					<Grid item minHeight={0} pt={1} px={2} pb={1} xs sx={{ overflowY: 'auto' }}>
						{renderTabContent}
					</Grid>
					{selectedTab !== 'Activity' && (
						<Grid item>
							<Stack direction={'row'} p={2} spacing={2} alignItems={'center'} justifyContent={'end'} display={'flex'}>
								<Button onClick={() => setOpen?.(false)} variant='outlined'>
									{language?.cancel || 'Cancel'}
								</Button>
								<Button
									variant='contained'
									sx={{ color: 'var(--whiteColor)' }}
									onClick={handleSave}
									// disabled={!hasChanges}
								>
									{language?.save || 'Save'}
								</Button>
							</Stack>
						</Grid>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
};

export default ImageDetailsSlider;
